import React, { useState } from 'react';
import { MessageSquare, Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/Label';
import { Textarea } from './ui/Textarea';
import { Toast } from './ui/Toast';
import ConfirmDialog from './ConfirmDialog';
import useCommunicationsParams from '../hooks/useCommunicationsParams';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import EnTeteSection from './parametrage/EnTeteSection';
import CarteReglages from './parametrage/CarteReglages';
import ChargementSection from './parametrage/ChargementSection';
import ChampSelect from './ui/ChampSelect';

/**
 * Paramétrage des communications de la page d'accueil.
 *
 * Page sœur de EventsSettingsSection : même architecture, mêmes briques.
 * Refondue le 11 septembre 2026 sur PARAMETRAGE-MODULES.md.
 *
 * Trois défauts corrigés au passage :
 *
 * 1. **Deux systèmes de retour.** Chaque message partait dans un bandeau ET
 *    dans un toast — la même phrase, deux fois, deux durées de vie.
 *
 * 2. **Le bouton portait le message d'erreur.** À la limite atteinte son
 *    libellé devenait « Maximum 5 communications atteint », et le bouton ne
 *    disait plus ce qu'il faisait. La raison du blocage s'affiche à côté.
 *
 * 3. **Le quota était annoncé trois fois** — un bandeau coloré, le titre de la
 *    colonne d'aperçu, et le libellé du bouton. Il l'est une fois, au compteur
 *    de la carte, là où la norme le place.
 */

const MAX_COMMUNICATIONS = 5;
const MAX_MESSAGE_LENGTH = 1000;

const ACCENT = '#246990';
const ACCENT_SOMBRE = '#1a4f6e';

const COULEURS = [
  { valeur: 'from-[#246990] to-[#3084b5]', nom: 'Bleu — Direction', apercu: '#246990' },
  { valeur: 'from-[#319190] to-[#63b5b4]', nom: 'Vert — Professeurs', apercu: '#319190' },
  { valeur: 'from-[#c66d59] to-[#ae7467]', nom: 'Orange — Vie scolaire', apercu: '#c66d59' },
  { valeur: 'from-[#8b5a8c] to-[#9d4edd]', nom: 'Violet — Administration', apercu: '#8b5a8c' },
];

const CommunicationsSettingsSection = ({ onBackToDashboard }) => {
  const {
    communications,
    addCommunication,
    deleteCommunication,
    loading: communicationsLoading,
  } = useCommunicationsParams();

  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const avertir = (message, type = 'error') => setToast({ show: true, message, type });
  const confirmer = (message) => setToast({ show: true, message, type: 'success' });

  const [newCommunication, setNewCommunication] = useState({
    source: '',
    message: '',
    color_gradient: COULEURS[0].valeur,
  });

  const quotaAtteint = communications.length >= MAX_COMMUNICATIONS;
  const champsManquants = !newCommunication.source.trim() || !newCommunication.message.trim();
  const couleurChoisie =
    COULEURS.find((c) => c.valeur === newCommunication.color_gradient) || COULEURS[0];

  const handleAddCommunication = async () => {
    if (quotaAtteint) {
      avertir(`Maximum ${MAX_COMMUNICATIONS} communications autorisées`);
      return;
    }
    if (champsManquants) {
      avertir('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (newCommunication.message.length > MAX_MESSAGE_LENGTH) {
      avertir(`Le message ne peut pas dépasser ${MAX_MESSAGE_LENGTH} caractères`);
      return;
    }

    setSaving(true);
    try {
      const result = await addCommunication(newCommunication);
      if (result.success) {
        confirmer('Communication ajoutée');
        setNewCommunication({ source: '', message: '', color_gradient: COULEURS[0].valeur });
      } else {
        avertir(result.error || "Erreur lors de l'ajout");
      }
    } catch (error) {
      avertir('Erreur de connexion au serveur');
    } finally {
      setSaving(false);
    }
  };

  // Meme regle que pour les evenements : `ConfirmDialog` laisse la fermeture au
  // parent, qui montre l'attente puis ferme une fois la suppression faite.
  const handleDeleteCommunication = async () => {
    if (!itemToDelete || deleting) return;
    setDeleting(true);
    try {
      const result = await deleteCommunication(itemToDelete.id);
      if (result.success) {
        confirmer('Communication supprimée');
        setShowDeleteConfirm(false);
        setItemToDelete(null);
      } else {
        // Echec : le dialogue reste ouvert pour permettre un nouvel essai.
        avertir(result.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      avertir('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const enTete = (
    <EnTeteParametrage
      onRetour={onBackToDashboard}
      icone={MessageSquare}
      titre="Paramétrage des communications"
      sousTitre="Messages de la direction affichés sur la page d'accueil"
    />
  );

  if (communicationsLoading) {
    return (
      <div className="w-full">
        {enTete}
        <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
          <ChargementSection accent={ACCENT} libelle="Chargement des communications…" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {enTete}

      <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
        <div className="space-y-5">
          <EnTeteSection
            icone={MessageSquare}
            titre="Communications de la page d'accueil"
            sousTitre={`${MAX_COMMUNICATIONS} messages au maximum, affichés dans l'ordre`}
            accent={ACCENT}
            accentSombre={ACCENT_SOMBRE}
          />

          {/* ------------------------------------------------------------ */}
          {/* Ajouter                                                       */}
          {/* ------------------------------------------------------------ */}
          <CarteReglages
            titre="Ajouter une communication"
            accent={couleurChoisie.apercu}
            compteur={`${communications.length}/${MAX_COMMUNICATIONS} utilisées`}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="text-sm text-gray-700">Source / expéditeur *</Label>
                <Input
                  value={newCommunication.source}
                  onChange={(e) =>
                    setNewCommunication((prev) => ({ ...prev, source: e.target.value }))
                  }
                  placeholder="Direction, Vie scolaire, CPE…"
                  maxLength={100}
                  disabled={quotaAtteint}
                  className="mt-1 h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>

              <div>
                <Label className="text-sm text-gray-700">Couleur</Label>
                <div className="mt-1 flex items-center gap-2">
                  <div className="flex-1">
                    <ChampSelect
                      valeur={newCommunication.color_gradient}
                      onChange={(valeur) =>
                        setNewCommunication((prev) => ({ ...prev, color_gradient: valeur }))
                      }
                      desactive={quotaAtteint}
                      options={COULEURS.map((couleur) => ({
                        valeur: couleur.valeur,
                        libelle: couleur.nom
                      }))}
                    />
                  </div>
                  <div
                    className="h-9 w-9 flex-shrink-0 rounded-lg border border-gray-200"
                    style={{ backgroundColor: couleurChoisie.apercu }}
                    aria-hidden="true"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <Label className="text-sm text-gray-700">Message *</Label>
                <Textarea
                  value={newCommunication.message}
                  onChange={(e) =>
                    setNewCommunication((prev) => ({ ...prev, message: e.target.value }))
                  }
                  placeholder="Contenu du message"
                  rows={4}
                  maxLength={MAX_MESSAGE_LENGTH}
                  disabled={quotaAtteint}
                  className="mt-1 resize-none rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
                <p className="mt-1.5 text-[11px] text-gray-400">
                  {MAX_MESSAGE_LENGTH - newCommunication.message.length} caractères restants
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
              <Button
                onClick={handleAddCommunication}
                disabled={saving || quotaAtteint || champsManquants}
                className="h-9 min-w-[168px] justify-center gap-2 bg-[#246990] hover:bg-[#1f5a7a] text-white disabled:bg-gray-300"
              >
                <Plus className="w-4 h-4" />
                {saving ? 'Ajout…' : 'Ajouter la communication'}
              </Button>

              {quotaAtteint && (
                <p className="text-xs text-amber-700">
                  Les {MAX_COMMUNICATIONS} emplacements sont occupés. Supprimez-en un pour en
                  ajouter un autre.
                </p>
              )}
              {!quotaAtteint && champsManquants && (
                <p className="text-xs text-gray-400">
                  Une source et un message sont requis.
                </p>
              )}
            </div>
          </CarteReglages>

          {/* ------------------------------------------------------------ */}
          {/* Ce qui est publié                                             */}
          {/* ------------------------------------------------------------ */}
          <CarteReglages
            titre="Publiées sur la page d'accueil"
            accent={ACCENT}
            compteur={`${communications.length} message${communications.length > 1 ? 's' : ''}`}
          >
            {communications.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-10">
                <MessageSquare size={28} className="text-gray-300" />
                <p className="text-sm text-gray-500">Aucune communication publiée</p>
                <p className="text-[11px] text-gray-400">
                  Celles que vous ajoutez apparaissent ici, dans l'ordre d'affichage.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {communications.map((comm) => (
                  <div
                    key={comm.id}
                    className={`group relative rounded-xl bg-gradient-to-r ${comm.color_gradient} p-4 text-white shadow-sm`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold">{comm.source}</p>
                        <p className="mt-1 text-sm leading-relaxed text-white/90">{comm.message}</p>
                        <p className="mt-2 text-[11px] text-white/70">
                          Ordre {comm.ordre} · {comm.message.length} caractères
                          {comm.updated_at && (
                            <> · {new Date(comm.updated_at).toLocaleDateString('fr-FR')}</>
                          )}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setItemToDelete(comm);
                          setShowDeleteConfirm(true);
                        }}
                        aria-label={`Supprimer la communication de ${comm.source}`}
                        className="flex-shrink-0 rounded-md p-1.5 text-white/70 opacity-0 transition-opacity hover:bg-white/20 hover:text-white group-hover:opacity-100 focus:opacity-100"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CarteReglages>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => { if (!deleting) { setShowDeleteConfirm(false); setItemToDelete(null); } }}
        onConfirm={handleDeleteCommunication}
        isLoading={deleting}
        title="Supprimer la communication"
        description={`Êtes-vous sûr de vouloir supprimer la communication de « ${itemToDelete?.source} » ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="warning"
      />
    </div>
  );
};

export default CommunicationsSettingsSection;